import { useMemo } from 'react'
import { investigationEvidence } from '@/components/knowledge-graph-view/face-visibility'
import { snippetNodeId } from '@/features/source-snippets/object-set'
import { ADVANCED_FACE_DETECTION_EXTENSION_ID, useExtensionEnabled } from '@/hooks/use-extensions'
import { useInvestigationsStore } from '@/stores/investigations-store-provider'
import type { SourceSnippet } from '@/types/source-snippet'

export interface ObjectSetSnapshot {
  /**
   * The set of entity IDs in the Active Investigation, or `null` when no
   * investigation is active (case-wide / "bird's eye" mode).
   *
   * When non-null, all main views (Map, Graph, Timeline) should render only
   * entities in this set. Filters within a view further narrow it.
   */
  ids: Set<string> | null
  memberIds: Set<string> | null
  snippets: readonly SourceSnippet[]

  /** Convenience: `ids != null && ids.size > 0`. */
  isPopulated: boolean

  /** Number of entities in the Object Set, or 0 when empty / null. */
  size: number

  /** Source of the current Object Set. */
  source: 'transient' | 'saved' | null
}

/**
 * Returns the Object Set of the currently active investigation.
 *
 * Object Set is the single shared scope across Map / Graph / Timeline. When
 * `ids === null`, no investigation is active — views should render their
 * empty-state prompts (insights + Ask Xendrel) instead of case-wide data.
 *
 * Must be used inside `<InvestigationsStoreProvider>`.
 */
export function useObjectSet(visibleGraphOnly = false): ObjectSetSnapshot {
  const transient = useInvestigationsStore(s => s.transient)
  const activeId = useInvestigationsStore(s => s.activeId)
  const active = useInvestigationsStore(s =>
    activeId ? (s.saved.find(i => i.id === activeId) ?? null) : null
  )
  // Saved faces leave the set while Advanced Face Detection is off (ADR-0051).
  const { enabled: faceDetection } = useExtensionEnabled(ADVANCED_FACE_DETECTION_EXTENSION_ID)

  return useMemo<ObjectSetSnapshot>(() => {
    const graph = transient?.graphView ?? active?.graphView
    if (!graph) {
      return { ids: null, memberIds: null, snippets: [], isPopulated: false, size: 0, source: null }
    }
    const { nodes } = investigationEvidence(graph.nodes, [], {
      graphOnly: visibleGraphOnly,
      expandedFaceIds: graph.view.expandedFaceIds,
      faceDetection,
    })
    const ids = new Set(nodes.map(n => n.id))
    const snippets = graph.snippets ?? []
    const size = ids.size + snippets.length
    const memberIds = new Set([...ids, ...snippets.map(item => snippetNodeId(item.id))])
    return {
      ids,
      memberIds,
      snippets,
      isPopulated: size > 0,
      size,
      source: transient ? 'transient' : 'saved',
    }
  }, [transient, active, visibleGraphOnly, faceDetection])
}
